import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
    listNotifications,
    getUnreadCount,
    markAsRead as apiMarkAsRead,
    markAllAsRead as apiMarkAllAsRead,
    deleteNotification as apiDeleteNotification,
} from '../api/v1/notifications';
import { toast } from 'react-toastify';
import { getAccessToken } from '../lib/api';
import { useTranslation } from 'react-i18next';
import {
    applyLocalReadState,
    clearPersistedReadIds,
    loadPersistedReadIds,
    persistReadIds,
    resolveUnreadCount,
} from '../lib/notifications/notificationReadState';


const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const { i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isUsingMockData] = useState(false);
    const readLocallyRef = useRef(loadPersistedReadIds());


    const getSessionToken = () => getAccessToken();

    const fetchNotificationsData = useCallback(async (quiet = false) => {
        if (!isAuthenticated) return;

        if (!quiet) setLoading(true);
        setError(null);

        try {
            const token = getSessionToken();

            const [countRes, listRes] = await Promise.all([
                getUnreadCount(token),
                listNotifications({ limit: 50, skip: 0 }, token),
            ]);

            const apiCount = countRes?.count !== undefined ? countRes.count : 0;
            const rawItems = listRes?.items || [];
            const items = applyLocalReadState(rawItems, readLocallyRef.current);

            setNotifications(items);
            setUnreadCount(resolveUnreadCount(apiCount, rawItems, readLocallyRef.current));
        } catch (err) {
            console.error('Notifications API error:', err);
            setNotifications([]);
            setUnreadCount(0);
            setError(err.message || 'Failed to fetch notifications');
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [isAuthenticated]);

    const markAsRead = useCallback(
        async (recipientId) => {
            if (!recipientId) return;

            let shouldCallApi = false;

            setNotifications((prev) => {
                const target = prev.find((n) => n.recipient_id === recipientId);
                if (!target || target.is_read) return prev;

                shouldCallApi = true;
                readLocallyRef.current.add(recipientId);
                persistReadIds(readLocallyRef.current);

                const next = applyLocalReadState(prev, readLocallyRef.current);
                setUnreadCount(next.filter((n) => !n.is_read).length);
                return next;
            });

            if (!shouldCallApi) return;
            if (isUsingMockData) return;

            try {
                const token = getSessionToken();
                await apiMarkAsRead(recipientId, token);
                // Do not call getUnreadCount here — backend may return stale count (see API test script)
            } catch (err) {
                console.error('Failed to mark notification as read:', err);
                readLocallyRef.current.delete(recipientId);
                persistReadIds(readLocallyRef.current);
                toast.error(
                    err.message ||
                        (isRtl ? 'تعذر تحديث حالة الإشعار' : 'Could not update notification status')
                );
                fetchNotificationsData(true);
            }
        },
        [isUsingMockData, fetchNotificationsData, isRtl]
    );

    const markAllAsRead = useCallback(async () => {
        setNotifications((prev) => {
            prev.forEach((n) => readLocallyRef.current.add(n.recipient_id));
            persistReadIds(readLocallyRef.current);
            const next = applyLocalReadState(
                prev.map((n) => ({
                    ...n,
                    is_read: true,
                    read_at: n.read_at ?? new Date().toISOString(),
                })),
                readLocallyRef.current
            );
            setUnreadCount(0);
            return next;
        });

        if (isUsingMockData) return;

        try {
            const token = getSessionToken();
            await apiMarkAllAsRead(token);
            toast.success(
                isRtl ? 'تم تحديد كل الإشعارات كمقروءة' : 'All notifications marked as read'
            );
        } catch (err) {
            console.error('Failed to mark all as read:', err);
            toast.error(
                err.message ||
                    (isRtl ? 'تعذر تحديد كل الإشعارات كمقروءة' : 'Could not mark all as read')
            );
            fetchNotificationsData(true);
        }
    }, [isUsingMockData, fetchNotificationsData, isRtl]);

    const deleteNotification = useCallback(
        async (recipientId, options = {}) => {
            const silent = typeof options === 'boolean' ? options : Boolean(options?.silent);
            const itemToDelete = notifications.find((n) => n.recipient_id === recipientId || n.id === recipientId);
            const wasUnread = itemToDelete && !itemToDelete.is_read;

            setNotifications((prev) => prev.filter((n) => n.recipient_id !== recipientId && n.id !== recipientId));
            if (wasUnread) {
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
            readLocallyRef.current.delete(recipientId);
            persistReadIds(readLocallyRef.current);

            if (isUsingMockData) return;

            try {
                const token = getSessionToken();
                await apiDeleteNotification(recipientId, token);
                if (!silent) {
                    toast.success(isRtl ? 'تم حذف الإشعار' : 'Notification deleted');
                }
            } catch (err) {
                console.error('Failed to delete notification:', err);
                if (!silent) {
                    toast.error(
                        err.message || (isRtl ? 'تعذر حذف الإشعار' : 'Could not delete notification')
                    );
                }
                fetchNotificationsData(true);
            }
        },
        [notifications, isUsingMockData, fetchNotificationsData, isRtl]
    );

    useEffect(() => {
        if (!isAuthenticated) {
            setNotifications([]);
            setUnreadCount(0);
            readLocallyRef.current = new Set();
            clearPersistedReadIds();
            return;
        }

        readLocallyRef.current = loadPersistedReadIds();
        fetchNotificationsData();

        const intervalId = setInterval(() => {
            fetchNotificationsData(true);
        }, 60000);

        return () => clearInterval(intervalId);
    }, [isAuthenticated, fetchNotificationsData]);

    const mergeWithLocalRead = useCallback(
        (items) => applyLocalReadState(items, readLocallyRef.current),
        []
    );

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                error,
                isUsingMockData,
                fetchNotifications: fetchNotificationsData,
                markAsRead,
                markAllAsRead,
                deleteNotification,
                mergeWithLocalRead,
            }}
        >
            {children}

        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
